import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Upload, X, Image as ImageIcon, Loader2 } from "lucide-react";
import { useAuth, authHeaders } from "../AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Inline uploader + library picker.
 *
 * Props:
 *   - value: current image URL (relative like /api/uploads/xxx.jpg or a full URL)
 *   - onChange(url|"")
 *   - label: string above the field
 *   - testid: base test id
 *   - allowClear: show a Remove button (default true)
 *   - aspect: "square" | "landscape" (default "landscape") — for preview shape
 */
export default function ImagePicker({ value, onChange, label, testid = "image-picker", allowClear = true, aspect = "landscape" }) {
  const { token } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [showLibrary, setShowLibrary] = useState(false);
  const [library, setLibrary] = useState([]);
  const [libLoading, setLibLoading] = useState(false);
  const inputRef = useRef(null);

  const resolvedUrl = value ? (value.startsWith("http") ? value : `${BACKEND_URL}${value}`) : "";
  const previewClass = aspect === "square" ? "aspect-square" : "aspect-video";

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError("Image is too large (max 8 MB)");
      return;
    }
    setError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/upload`, fd, {
        headers: { ...authHeaders(token), "Content-Type": "multipart/form-data" },
      });
      onChange(res.data.url);
    } catch (e) {
      setError(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const openLibrary = async () => {
    setShowLibrary(true);
    setLibLoading(true);
    try {
      const res = await axios.get(`${API}/admin/uploads`, { headers: authHeaders(token) });
      setLibrary(res.data.uploads || []);
    } catch {
      setLibrary([]);
    } finally {
      setLibLoading(false);
    }
  };

  return (
    <div data-testid={testid}>
      {label && <label className="qw-overline block mb-2">{label}</label>}
      <div className="border border-border bg-background/50 p-3 flex items-start gap-4">
        {/* Preview */}
        <div className={`w-32 shrink-0 ${previewClass} border border-border bg-muted/30 overflow-hidden flex items-center justify-center`}>
          {resolvedUrl ? (
            <img src={resolvedUrl} alt="preview" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon size={22} strokeWidth={0.8} className="text-muted-foreground" />
          )}
        </div>

        {/* Actions */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              data-testid={`${testid}-upload-btn`}
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-2 px-3 py-2 border border-primary text-primary text-[0.65rem] tracking-wider uppercase hover:bg-primary hover:text-primary-foreground transition-colors disabled:opacity-50"
            >
              {uploading ? <Loader2 size={12} strokeWidth={1.2} className="animate-spin" /> : <Upload size={12} strokeWidth={1.2} />}
              {uploading ? "Uploading…" : "Upload"}
            </button>
            <button
              type="button"
              data-testid={`${testid}-library-btn`}
              onClick={openLibrary}
              className="inline-flex items-center gap-2 px-3 py-2 border border-border text-foreground/85 text-[0.65rem] tracking-wider uppercase hover:border-primary hover:text-primary transition-colors"
            >
              <ImageIcon size={12} strokeWidth={1.2} />
              Library
            </button>
            {allowClear && value && (
              <button
                type="button"
                data-testid={`${testid}-clear-btn`}
                onClick={() => onChange("")}
                className="inline-flex items-center gap-2 px-3 py-2 border border-border text-muted-foreground text-[0.65rem] tracking-wider uppercase hover:border-destructive hover:text-destructive transition-colors"
              >
                <X size={12} strokeWidth={1.2} />
                Remove
              </button>
            )}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            data-testid={`${testid}-file-input`}
            className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
          />
          {value && (
            <p className="text-[0.6rem] tracking-wider text-muted-foreground mt-3 truncate">{value}</p>
          )}
          {error && <p className="text-xs text-destructive mt-2">{error}</p>}
        </div>
      </div>

      {/* Library modal */}
      {showLibrary && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center px-4 py-8 bg-background/85 backdrop-blur-md" data-testid={`${testid}-library-modal`}>
          <div className="bg-background border border-border shadow-2xl w-full max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div>
                <p className="qw-overline">Media library</p>
                <h3 className="font-serif text-2xl text-foreground">Choose an image</h3>
              </div>
              <button
                onClick={() => setShowLibrary(false)}
                data-testid={`${testid}-library-close`}
                className="text-foreground/60 hover:text-primary"
                aria-label="Close library"
              >
                <X size={20} strokeWidth={1.2} />
              </button>
            </div>
            <div className="overflow-y-auto px-6 py-6">
              {libLoading ? (
                <p className="text-muted-foreground py-12 text-center">Loading…</p>
              ) : library.length === 0 ? (
                <div className="py-16 text-center">
                  <ImageIcon size={32} strokeWidth={0.8} className="text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground text-sm">No images uploaded yet. Click "Upload" to add your first.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {library.map((f) => (
                    <button
                      key={f.filename}
                      type="button"
                      data-testid={`${testid}-library-item-${f.filename}`}
                      onClick={() => {
                        onChange(f.url);
                        setShowLibrary(false);
                      }}
                      className="group aspect-square border border-border hover:border-primary transition-colors overflow-hidden bg-muted/30"
                    >
                      <img
                        src={`${BACKEND_URL}${f.url}`}
                        alt={f.filename}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
